/* eslint-disable */
// Hand-authored holiday loader scene (International Coffee Day) in the handoff idiom; returns an inline-SVG string.

const rnd = (a, b) => a + Math.random() * (b - a);

/* ---- International Coffee Day (Oct 1): a hero steaming cup on a saucer with
        latte-art, on a warm cafe backdrop. Signature motion is organic curling
        steam — three staggered wisps rising and fading. Traits rolled per call:
        warm MORNING (window light) vs cosy EVENING (lamp glow) palette, and a
        latte-art HEART vs LEAF (rosetta). Gradient ids salted per render so
        simultaneously-mounted thumbnails keep their own rolls. The cup sits
        left-of-centre so the centred BARE badge floats over open space, steam
        curling up beside it. ---- */
function coffeeScene() {
  const pick = (a) => a[Math.floor(Math.random() * a.length)];
  const uid  = 'c' + Math.random().toString(36).slice(2, 7);

  // rolled traits
  const time = pick(['morning', 'evening']);
  const art  = pick(['heart', 'leaf', 'heart']);

  const T = time === 'morning'
    ? { sky: ['#ffedd2', '#ffdcac', '#f4c07f'], table: ['#cf9257', '#a5673a'], edge: '#8a5330',
        glow: ['#fff6dc', '#ffe6ad'], orb: ['#fffaf0', '#ffe08f'],
        cupHi: '#fffdf9', cupMid: '#f2e8d6', cupLo: '#cdbca1', cream: '#f2e6cd', spark: '#fff3d2' }
    : { sky: ['#3a2216', '#5a361f', '#7d4a2a'], table: ['#3c2517', '#241408'], edge: '#170c05',
        glow: ['#ffcf83', '#ff9d42'], orb: ['#ffe6b0', '#ff9d42'],
        cupHi: '#f2ecdd', cupMid: '#d6c6ad', cupLo: '#93795c', cream: '#ecdcbc', spark: '#ffcf87' };

  const CX = 114, CY = 172;           // saucer centre on the table
  const SEAT = 9;                     // drop the cup body this far so its base rests in the saucer

  // ---------- a coffee bean: glossy ovoid with the signature centre crease ----------
  const bean = (x, y, s, rot) =>
    `<g transform="translate(${x},${y}) rotate(${rot}) scale(${s})">`
    + `<ellipse rx="7" ry="4.6" fill="url(#${uid}bean)"/>`
    + `<path d="M-5.2,0 C -2,-2.3 2,-2.3 5.2,0 C 2,2.3 -2,2.3 -5.2,0" fill="none" stroke="#28130a" stroke-width="1.1" stroke-linecap="round"/>`
    + `<ellipse cx="-2" cy="-1.7" rx="2.6" ry="1.1" fill="#fff" opacity=".14"/>`
    + `</g>`;

  // ---------- latte-art, drawn flat then squashed onto the coffee's ellipse ----------
  const heartArt = `<path d="M0,7 C -10,-2 -12,-9 -6,-9 C -2,-9 0,-6 0,-4 C 0,-6 2,-9 6,-9 C 12,-9 10,-2 0,7 Z" fill="${T.cream}" opacity=".95"/>`;
  const leafArt =
      `<path d="M0,-10 L0,8" stroke="${T.cream}" stroke-width="1.7" stroke-linecap="round" opacity=".95"/>`
    + `<path d="M0,-7 q-8,1 -9,5 q6,-1 9,-2 Z M0,-7 q8,1 9,5 q-6,-1 -9,-2 Z" fill="${T.cream}" opacity=".95"/>`
    + `<path d="M0,-2 q-7,1 -7.5,4.5 q5,-1 7.5,-1.8 Z M0,-2 q7,1 7.5,4.5 q-5,-1 -7.5,-1.8 Z" fill="${T.cream}" opacity=".95"/>`
    + `<path d="M0,3 q-5,1 -5,3.4 q3.6,-.8 5,-1.4 Z M0,3 q5,1 5,3.4 q-3.6,-.8 -5,-1.4 Z" fill="${T.cream}" opacity=".95"/>`;
  const latteArt = art === 'heart' ? heartArt : leafArt;

  // ---------- the cup: soft-shadowed saucer, left-side handle, porcelain body,
  //            thin rim, coffee surface + art, catchlight ----------
  const cup =
    `<g transform="translate(${CX},${CY})">`
      + `<ellipse cx="4" cy="15" rx="58" ry="9" fill="url(#${uid}shadow)"/>`
      // saucer
      + `<ellipse cx="0" cy="9" rx="53" ry="11" fill="${T.cupLo}"/>`
      + `<ellipse cx="0" cy="5" rx="53" ry="10.5" fill="url(#${uid}cup)"/>`
      + `<ellipse cx="0" cy="5" rx="27" ry="5.4" fill="rgba(0,0,0,.07)"/>`
      // contact shadow where the cup base meets the plate
      + `<ellipse cx="0" cy="${SEAT + 1}" rx="25" ry="4.3" fill="rgba(0,0,0,.18)"/>`
      // the cup itself, dropped by SEAT so its base sits in the saucer well
      + `<g transform="translate(0,${SEAT})">`
        // handle (left, pointing away from the badge)
        + `<path d="M-31,-54 C -51,-57 -57,-31 -34,-21 C -39,-26 -46,-33 -44,-40 C -42,-48 -37,-50 -29,-47 Z" fill="url(#${uid}cup)" stroke="${T.cupLo}" stroke-width="0.6"/>`
        // body
        + `<path d="M-34,-60 C -35,-40 -29,-14 -22,-6 Q 0,4 22,-6 C 29,-14 35,-40 34,-60 A 34 8 0 0 1 -34,-60 Z" fill="url(#${uid}cup)"/>`
        + `<ellipse cx="-19" cy="-40" rx="6" ry="22" transform="rotate(6 -19 -40)" fill="#fff" opacity=".16"/>`
        // rim + coffee + art
        + `<ellipse cx="0" cy="-60" rx="34" ry="8" fill="url(#${uid}cup)"/>`
        + `<ellipse cx="0" cy="-59" rx="30" ry="6.8" fill="#2c160a"/>`
        + `<ellipse cx="0" cy="-59" rx="28.5" ry="6.2" fill="url(#${uid}coffee)"/>`
        + `<g transform="translate(0,-59) scale(0.92,0.42)">${latteArt}</g>`
        + `<ellipse cx="-10" cy="-61" rx="9" ry="2.1" fill="#fff" opacity=".12"/>`
      + `</g>`
    + `</g>`;

  // ---------- three organic steam wisps rising from the rim, curling + fading ----------
  const wisp = (dx, h, w, sw, dur, wob, dl) => {
    const d = `M0,0 C ${-w},${(-h * 0.24).toFixed(1)} ${w},${(-h * 0.5).toFixed(1)} 0,${(-h * 0.72).toFixed(1)}`
            + ` C ${-w},${(-h * 0.9).toFixed(1)} ${(w * 0.6).toFixed(1)},${(-h * 0.98).toFixed(1)} 0,${-h}`;
    return `<g transform="translate(${CX + dx},${CY - 61 + SEAT})">`
      + `<g class="steam" style="animation-duration:${dur}s;animation-delay:${dl}s">`
      + `<g class="steam-wob" style="animation-duration:${wob}s;animation-delay:${dl}s">`
      + `<path d="${d}" fill="none" stroke="url(#${uid}steam)" stroke-width="${sw}" stroke-linecap="round"/>`
      + `</g></g></g>`;
  };
  const steam = wisp(-9, 50, 6, 5.5, 3.9, 2.7, -0.3)
              + wisp(7, 58, 7, 6.5, 4.6, 3.1, -1.6)
              + wisp(-1, 44, 5, 4.5, 3.4, 2.4, -2.7);

  // ---------- scattered beans on the table (kept clear of the centre) ----------
  const beans = bean(232, 190, 1.05, 22) + bean(258, 182, 0.9, -34)
              + bean(284, 194, 1.0, 8) + bean(60, 196, 0.95, -18);

  // ---------- warm light source (window sun / pendant lamp) + subtle sparkle ----------
  const light = `<g transform="translate(300,42)"><g class="glow"><circle r="58" fill="url(#${uid}glow)"/></g>`
    + `<circle r="19" fill="url(#${uid}orb)"/><circle r="19" fill="none" stroke="#fff" stroke-width="0.8" opacity=".3"/></g>`;
  const star = (s) => { const b = (s * 0.3).toFixed(2), S = s.toFixed(2);
    return `M0,-${S} L${b},-${b} L${S},0 L${b},${b} L0,${S} L-${b},${b} L-${S},0 L-${b},-${b} Z`; };
  const SP = [[300, 78, 4, 0], [66, 64, 3.4, 0.7], [322, 150, 3, 1.3], [40, 128, 3.2, 1.9]];
  const sparks = SP.map(([x, y, s, d]) =>
    `<g class="twinkle" transform="translate(${x},${y})" style="animation-delay:${d}s"><path d="${star(s)}" fill="${T.spark}"/></g>`).join('');

  // soft halo behind the badge for cohesion
  const centreGlow = `<g transform="translate(180,108)"><g class="glow"><ellipse rx="88" ry="66" fill="url(#${uid}cglow)"/></g></g>`;

  return `<svg class="scene-svg" viewBox="0 0 360 220" preserveAspectRatio="xMidYMid slice" role="img" aria-label="A steaming cup of coffee with latte-art on a warm cafe table.">
    <defs>
      <linearGradient id="${uid}sky" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stop-color="${T.sky[0]}"/><stop offset="0.55" stop-color="${T.sky[1]}"/><stop offset="1" stop-color="${T.sky[2]}"/>
      </linearGradient>
      <linearGradient id="${uid}table" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stop-color="${T.table[0]}"/><stop offset="1" stop-color="${T.table[1]}"/>
      </linearGradient>
      <linearGradient id="${uid}cup" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0" stop-color="${T.cupLo}"/><stop offset="0.22" stop-color="${T.cupHi}"/><stop offset="0.5" stop-color="${T.cupHi}"/><stop offset="0.78" stop-color="${T.cupMid}"/><stop offset="1" stop-color="${T.cupLo}"/>
      </linearGradient>
      <radialGradient id="${uid}coffee" cx="0.4" cy="0.32" r="0.72">
        <stop offset="0" stop-color="#8a5730"/><stop offset="0.55" stop-color="#5a3418"/><stop offset="1" stop-color="#33190a"/>
      </radialGradient>
      <radialGradient id="${uid}bean" cx="0.38" cy="0.3" r="0.75">
        <stop offset="0" stop-color="#7a4a28"/><stop offset="0.6" stop-color="#4f2d15"/><stop offset="1" stop-color="#341a0a"/>
      </radialGradient>
      <linearGradient id="${uid}steam" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stop-color="${time === 'morning' ? '#fffaf0' : '#ffe9cf'}" stop-opacity="0"/><stop offset="0.55" stop-color="${time === 'morning' ? '#fffaf0' : '#ffe9cf'}" stop-opacity="0.5"/><stop offset="1" stop-color="${time === 'morning' ? '#fffaf0' : '#ffe9cf'}" stop-opacity="0.68"/>
      </linearGradient>
      <radialGradient id="${uid}glow" cx="0.5" cy="0.5" r="0.5">
        <stop offset="0" stop-color="${T.glow[1]}" stop-opacity=".6"/><stop offset="1" stop-color="${T.glow[1]}" stop-opacity="0"/>
      </radialGradient>
      <radialGradient id="${uid}orb" cx="0.5" cy="0.42" r="0.5">
        <stop offset="0" stop-color="${T.orb[0]}"/><stop offset="1" stop-color="${T.orb[1]}"/>
      </radialGradient>
      <radialGradient id="${uid}cglow" cx="0.5" cy="0.5" r="0.5">
        <stop offset="0" stop-color="#fff6e4" stop-opacity=".5"/><stop offset="0.55" stop-color="#ffe9c8" stop-opacity=".24"/><stop offset="1" stop-color="#ffe9c8" stop-opacity="0"/>
      </radialGradient>
      <radialGradient id="${uid}shadow" cx="0.5" cy="0.5" r="0.5">
        <stop offset="0" stop-color="#160b04" stop-opacity=".34"/><stop offset="0.7" stop-color="#160b04" stop-opacity=".12"/><stop offset="1" stop-color="#160b04" stop-opacity="0"/>
      </radialGradient>
    </defs>
    <rect width="360" height="220" fill="url(#${uid}sky)"/>
    ${light}
    <path d="M0,150 L360,150 L360,220 L0,220 Z" fill="url(#${uid}table)"/>
    <rect y="148" width="360" height="3.2" fill="${T.edge}" opacity=".45"/>
    ${centreGlow}
    ${sparks}
    ${cup}
    ${steam}
    ${beans}
  </svg>`;
}

export { coffeeScene };
